import React, { useEffect, useState } from "react";
import { BASE_URL } from "../../../../config";
import { apiFetch } from "../../../../apiFetch";
import { useLocation } from "react-router-dom";
import moment from "moment";
import "./signingoffer.css";
import PlayerOfferNeonPanel from "../../../PlayerOfferNeonPanel";

const parseDuration = (durationStr = "") => {
  const lower = String(durationStr).toLowerCase().trim();
  const number = parseInt(lower, 10);

  if (Number.isNaN(number)) return 0;

  if (lower.includes("year")) return number * 365 * 24 * 60 * 60 * 1000;
  if (lower.includes("month")) return number * 30 * 24 * 60 * 60 * 1000;
  if (lower.includes("day")) return number * 24 * 60 * 60 * 1000;
  if (lower.includes("hour")) return number * 60 * 60 * 1000;
  if (lower.includes("minute")) return number * 60 * 1000;
  return 0;
};

const isPaid = (offer) => {
  return (offer?.paymentStatus || "").toLowerCase() === "paid";
};

const getActivationStartTimeMs = (offer) => {
  const t =
    offer?.paidAt ||
    offer?.paymentPaidAt ||
    offer?.paymentCompletedAt ||
    offer?.paymentSuccessTime ||
    offer?.activationTime ||
    null;

  if (!t) return null;

  const ms = new Date(t).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const isContractActive = (offer) => {
  if (!offer) return false;

  if (offer.status === "terminated" || offer.status === "closed") {
    return false;
  }

  if (
    offer.status !== "accepted" ||
    offer.adminApproval !== "approved" ||
    !offer.contractDuration
  ) {
    return false;
  }

  if (!isPaid(offer)) return false;

  const startTime = getActivationStartTimeMs(offer);
  if (!startTime) return false;

  const durationMs = parseDuration(offer.contractDuration);
  if (!durationMs) return false;

  const endTime = startTime + durationMs;
  return Date.now() < endTime;
};

const hasContractExpired = (offer) => {
  if (!offer) return false;

  if (offer.status === "terminated" || offer.status === "closed") {
    return false;
  }

  if (
    offer.status !== "accepted" ||
    offer.adminApproval !== "approved" ||
    !offer.contractDuration
  ) {
    return false;
  }

  if (!isPaid(offer)) return false;

  const startTime = getActivationStartTimeMs(offer);
  if (!startTime) return false;

  const durationMs = parseDuration(offer.contractDuration);
  if (!durationMs) return false;

  return Date.now() >= startTime + durationMs;
};

function getRoleProfilePath(role, slugOrId) {
  if (!role || !slugOrId) return null;

  switch (String(role).toLowerCase()) {
    case "agent":
      return `/agent-profile/${slugOrId}`;
    case "scout":
      return `/scout-profile/${slugOrId}`;
    case "club":
      return `/club-profile/${slugOrId}`;
    default:
      return null;
  }
}

const toAbsoluteUrl = (maybePathOrUrl) => {
  if (!maybePathOrUrl) return "#";
  if (/^https?:\/\//i.test(maybePathOrUrl)) return maybePathOrUrl;

  try {
    return new URL(maybePathOrUrl, BASE_URL).toString();
  } catch {
    return "#";
  }
};

const SigningOffer = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [playerNotes, setPlayerNotes] = useState({});
  const [acceptedTerms, setAcceptedTerms] = useState({});
  const [selectedTab, setSelectedTab] = useState("offers");
  const location = useLocation();

  const state = location.state || {};
  const role = (state.role || "").toLowerCase();
  const userId = state.userId || localStorage.getItem("playerId") || null;

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const res = await apiFetch(
          `/api/offers?playerId=${encodeURIComponent(userId)}`
        );

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(`Failed to fetch offers (${res.status}): ${text}`);
        }

        const data = await res.json();
        setOffers(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching offers:", error);
        setError("Failed to fetch offers. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (userId && role === "player") {
      fetchOffers();
    } else {
      setError("Access denied. Only players can view signing offers.");
      setLoading(false);
    }
  }, [userId, role]);

  const hasActiveContract = offers.some((offer) => isContractActive(offer));

  const handleDecision = async (offerId, decision) => {
    try {
      const note = playerNotes[offerId] || "";

      const res = await apiFetch(`/api/offers/${offerId}/decision`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: decision,
          playerNote: note,
          deciderRole: "player",
          deciderId: Number(userId),
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Failed to ${decision} offer (${res.status}): ${text}`);
      }

      setOffers((prev) =>
        prev.map((offer) =>
          offer.id === offerId
            ? {
              ...offer,
              status: decision,
              signedByPlayer: decision === "accepted",
              adminApproval: decision === "closed" ? null : offer.adminApproval,
              adminApprovalTime:
                decision === "closed" ? null : offer.adminApprovalTime,
            }
            : offer
        )
      );

      setSelectedTab(decision === "accepted" ? "pendingApproval" : "rejected");
    } catch (error) {
      console.error(`Failed to ${decision} offer:`, error);
      alert(error.message || `Failed to ${decision} offer.`);
    }
  };

  const filteredOffers = [...offers]
    .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
    .filter((offer) => {
      if (selectedTab === "offers") return offer.status === "pending";
      if (selectedTab === "pendingApproval")
        return offer.status === "accepted" && offer.adminApproval === null;
      if (selectedTab === "accepted")
        return offer.status === "accepted" && offer.adminApproval === "approved";
      if (selectedTab === "rejected")
        return (
          offer.status === "closed" ||
          offer.status === "terminated" ||
          offer.adminApproval === "rejected"
        );
      return true;
    });

  const renderStatus = (offer) => {
    if (offer.status === "terminated") return "Terminated";

    if (offer.status === "accepted" && offer.adminApproval === null) {
      return "Awaiting admin approval";
    }

    if (offer.status === "accepted" && offer.adminApproval === "approved") {
      if (!isPaid(offer)) return "Awaiting Activation (Payment)";
      if (isContractActive(offer)) return "Active";
      if (hasContractExpired(offer)) return "Expired";
      return "Admin Approved";
    }

    if (offer.status === "closed" && offer.adminApproval === "rejected") {
      return "Rejected by admin";
    }

    if (offer.status === "closed") return "Rejected";

    return offer.status;
  };

  const buildProfileLink = (offer) => {
    const signedRole = (offer?.signedByRole || "").toLowerCase();

    const slug =
      offer?.senderSlug ||
      offer?.partnerSlug ||
      offer?.profileSlug ||
      offer?.agentSlug ||
      offer?.scoutSlug ||
      offer?.clubSlug ||
      null;

    const id = offer?.agentId || offer?.scoutId || offer?.clubId || null;

    const identifier = slug || id;
    if (!signedRole || !identifier) return null;

    return getRoleProfilePath(signedRole, identifier);
  };

  const titleForTab = (tab) => {
    if (tab === "offers") return "Offers";
    if (tab === "pendingApproval") return "Pending Approval";
    if (tab === "accepted") return "Accepted Offers";
    if (tab === "rejected") return "Rejected Offers";
    return tab;
  };

  return (
    <div className="container">
      <h1 className="title">Contract Offers</h1>
      <p className="subtitle">
        These are Offers you have received from Agents, Scouts, and Clubs...
      </p>

      <div className="toggle-bar">
        <div
          className={`toggle-option ${selectedTab === "offers" ? "active" : ""}`}
          onClick={() => setSelectedTab("offers")}
        >
          Offers
        </div>
        <div
          className={`toggle-option ${selectedTab === "pendingApproval" ? "active" : ""
            }`}
          onClick={() => setSelectedTab("pendingApproval")}
        >
          Pending Approval
        </div>
        <div
          className={`toggle-option ${selectedTab === "accepted" ? "active" : ""
            }`}
          onClick={() => setSelectedTab("accepted")}
        >
          Accepted Offers
        </div>
        <div
          className={`toggle-option ${selectedTab === "rejected" ? "active" : ""
            }`}
          onClick={() => setSelectedTab("rejected")}
        >
          Rejected Offers
        </div>
      </div>

      {loading ? (
        <p className="loading">Loading offers...</p>
      ) : error ? (
        <p className="error">{error}</p>
      ) : filteredOffers.length === 0 ? (
        <p className="no-offers">No {titleForTab(selectedTab)} available.</p>
      ) : (
        <div>
          {filteredOffers.map((offer) => {
            const profileLink = buildProfileLink(offer);

            const offerFromName =
              offer.agentName || offer.scoutName || offer.clubName || "Unknown";

            const offerFromRole = offer.signedByRole
              ? offer.signedByRole.charAt(0).toUpperCase() +
              offer.signedByRole.slice(1)
              : "Representative";

            const statusText = renderStatus(offer);
            const canDecide = offer.status === "pending";

            return (
              <div className="player-offer-block" key={offer.id}>
                <PlayerOfferNeonPanel
                  toAbsoluteUrl={toAbsoluteUrl}
                  offer={{
                    // ✅ Keep offer details the panel needs, but DO NOT pass scoutName/agentName/clubName
                    // if you don't want those rows displayed.
                    contractDuration: offer.contractDuration,
                    additionalNotes: offer.notes || "No notes",
                    uploadedContractUrl: offer.agentContractUrl || null,
                    // ✅ Remove this row if you don't want "Contract Policy: Read Official Policy"
                    // contractPolicyUrl: "/VSN_Player_Contract.docx",
                    submittedAt: offer.submittedAt,
                    statusText: statusText,
                    badge: { subtitle: "From", title: offerFromRole },
                  }}
                  onAccept={
                    canDecide
                      ? () => handleDecision(offer.id, "accepted")
                      : undefined
                  }
                  onReject={
                    canDecide
                      ? () => handleDecision(offer.id, "closed")
                      : undefined
                  }
                  header={
                    <div>
                      <div className="offer-from-row">
                        <span className="offer-from-label">Offer From: </span>
                        <span className="offer-from-value">
                          {offerFromRole} {offerFromName}{" "}
                        </span>
                        (
                        <a
                          href={profileLink || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="offer-from-link"
                          onClick={(e) => {
                            if (!profileLink) e.preventDefault();
                          }}
                        >
                          Profile Link
                        </a>
                        )
                      </div>

                      {offer.status === "pending" && (
                        <div className="player-policy-box">
                          <p className="player-policy-text">
                            <strong>Note:</strong> The contract duration is the
                            time you agree to be exclusively represented by the
                            offering agent, scout, or club. During this period,
                            you cannot accept other offers or enter new
                            agreements on VSN. You may be taken on trials and
                            considered for career opportunities arranged by your
                            representative.
                          </p>
                          <a
                            href="/VSN_Player_Contract.docx"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="player-policy-link"
                          >
                            📄 Read VSN Player Contract
                          </a>
                        </div>
                      )}
                    </div>
                  }
                >
                  {offer.status === "pending" && (
                    <div className="player-offer-actions-area">
                      <div style={{ marginTop: "12px" }}>
                        <label
                          htmlFor={`note-${offer.id}`}
                          style={{ color: "white", fontWeight: "bold" }}
                        >
                          Your Note (optional):
                        </label>
                        <textarea
                          id={`note-${offer.id}`}
                          className="textarea-note"
                          value={playerNotes[offer.id] || ""}
                          onChange={(e) =>
                            setPlayerNotes({
                              ...playerNotes,
                              [offer.id]: e.target.value,
                            })
                          }
                          rows={1}
                          placeholder="Write your message to the agent/club/scout...Keep it short"
                        />
                      </div>

                      <div style={{ margin: "10px 0" }}>
                        <label style={{ color: "white" }}>
                          <input
                            type="checkbox"
                            checked={acceptedTerms[offer.id] || false}
                            onChange={(e) =>
                              setAcceptedTerms({
                                ...acceptedTerms,
                                [offer.id]: e.target.checked,
                              })
                            }
                            style={{ marginRight: "6px" }}
                          />
                          Accept terms and conditions of the contract
                        </label>
                      </div>

                      <div className="offer-actions">
                        <button
                          onClick={() => handleDecision(offer.id, "accepted")}
                          className="btn accept-btn"
                          disabled={
                            hasActiveContract || !acceptedTerms[offer.id]
                          }
                          title={
                            hasActiveContract
                              ? "You already have an active contract."
                              : !acceptedTerms[offer.id]
                                ? "You must accept terms first."
                                : ""
                          }
                        >
                          Accept Offer
                        </button>

                        <button
                          onClick={() => handleDecision(offer.id, "closed")}
                          className="btn reject-btn"
                        >
                          Reject Offer
                        </button>
                      </div>
                    </div>
                  )}
                </PlayerOfferNeonPanel>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SigningOffer;



















